import { MatPasswordStrengthModule } from '@angular-material-extensions/password-strength';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { I18N_PROVIDERS, LANGUAGE_PROVIDER, LanguageInterceptor } from '@modeso/aumico-internationalization';
import { AumicoLayoutModule2 } from '@modeso/aumico-layout';
import { AumicoAiAgentsConfig, AumicoAiAgentsModule } from '@modeso/aumico-lib-ai-agents-fe';
import { AumicoAnnualReportConfig, AumicoAnnualReportModule } from '@modeso/aumico-lib-annualreport-fe';
import { AumicoCompaniesConfig, AumicoCompaniesModule } from '@modeso/aumico-lib-companies-fe';
import {
    AumicoIntegrationsConfig,
    AumicoLibIntegrationsFeModule,
    redactConnectSecretsForDevtools,
} from '@modeso/aumico-lib-integrations-fe';
import {
    AbcpDataModule,
    AumicoAbcpConfig,
    AumicoAbcpPollIntervalsToken,
    resolveAumicoAbcpPollIntervals,
} from '@modeso/aumico-lib-abcp-fe';
import { AumicoCoreModule } from '@modeso/aumico-lib-core-fe';
import { CpmmConfig } from '@modeso/aumico-lib-cpmm-fe';
import { AumicoNotesConfig, AumicoNotesModule, FX_NOTES_PRINT_ENGINE } from '@modeso/aumico-lib-notes-fe';
import { FxNotesPrintEngineService } from './microservices/ms-annual-reports/services/fx-notes-print-engine.service';
import { AumicoLibSettingsFeModule, AumicoSettingsConfig } from '@modeso/aumico-lib-settings-fe';
import { AumicoTemplatesConfig, AumicoTemplatesModule, AutomaticTransferOfAnnualResultService } from '@modeso/aumico-lib-templates-fe';
import { AuthHandler, UsersConfig, UsersModule } from '@modeso/aumico-lib-users-fe';
import { EffectsModule } from '@ngrx/effects';
import { MetaReducer, StoreModule, USER_PROVIDED_META_REDUCERS } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from "@sentry/angular";
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { I18NextModule } from 'angular-i18next';
import { QuillModule } from 'ngx-quill';
import { AumicoComponentsModule } from '../../../aumico-components/src/lib';
import { LocalStorageService } from '../../../aumico-lib-core-fe/src/lib/services/localStorage.service';
import { AumicoCpmmModule } from '../../../aumico-lib-cpmm-fe/src/lib';
import { CPMM_UI_FEATURE } from '../../../aumico-lib-cpmm-fe/src/lib/constants/constants';
import { AumicoProjectsModule, ProjectsConfig } from '../../../aumico-lib-projects-fe/src/lib';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ROOT_LOCAL_STORAGE_KEY, ROOT_STORAGE_KEYS } from './app.tokens';
import { AcceptPolicyDialogComponent } from './components/accept-policy-dialog/accept-policy-dialog.component';
import { AcceptPolicyToContinueUsingAumicoDialogComponent } from './components/accept-policy-to-continue-using-aumico-dialog/accept-policy-to-continue-using-aumico-dialog.component';
import { AppButtonComponent } from './components/app-button/app-button.component';
import { AppIntroComponent } from './components/app-intro/appintro.component';
import { ArTemplatesListDialogComponent } from './components/ar-templates-list-dialog/ar-templates-list-dialog.component';
import { ConfirmShareReportDialogComponent } from './components/confirm-share-report-dialog/confirm-share-report-dialog.component';
import { InvitationExpiredPage } from './components/invitation-expired/invitation-expired.component';
import { InviteDialogComponent } from './components/invite-dialog/invite-dialog.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { ReferralTeaserComponent } from './components/referral-invite/referral-invite.component';
import { ReorderReportSectionsDialogComponent } from './components/reorder-report-sections-dialog/reorder-report-sections-dialog.component';
import { SideHeadingComponent } from './components/side-heading/side-heading.component';
import { SideListItemComponent } from './components/side-list-item/side-list-item.component';
import { SideListComponent } from './components/side-list/side-list.component';
import { SideParagraphComponent } from './components/side-paragraph/side-paragraph.component';
import { SwitchToAutomaticTransferOfAnnualResultComponent } from './components/switch-to-automatic-transfer-of-annual-result/switch-to-automatic-transfer-of-annual-result.component';
import { ValuationOrderConfirmationDialogComponent } from './components/valuation-order-confirmation-dialog/valuation-order-confirmation-dialog.component';
import { ValuationReportAnnouncementDialogComponent } from './components/valuation-report-announcement-dialog/valuation-report-announcement-dialog.component';
import { ValuationReportDialogComponent } from './components/valuation-report-dialog/valuation-report-dialog.component';
import { RandomQuestionWithActionButtonComponent } from './components/welcomePage/randomQuestionWithActionButtonComponent/randomQuestionWithActionButton.component';
import { WelcomePageFooterComponent } from './components/welcomePage/welcomePageFooter/welcomePageFooter.component';
import { AbcpModule } from './microservices/abcp/abcp.module';
import { AbcpPage } from './microservices/abcp/abcp.page';
import { ClosingWorkflowPage } from './microservices/cpmm/closing-workflow/closing-workflow.page';
import { CpmmModule } from './microservices/cpmm/cpmm.module';
import { CpmmPage } from './microservices/cpmm/cpmm.page';
import { MandateManagementPage } from './microservices/cpmm/mandate-management/mandate-management.page';
import { TasksAndClosingAdvertisingPage } from './microservices/cpmm/tasks-and-closing-advertising/tasks-and-closing-advertising.page';
import { AnnualReportAOPPage } from './microservices/ms-annual-reports//aop/aop.page';
import { AccountMappingGermanDisplayComponent } from './microservices/ms-annual-reports/account-mapping/account-mapping-german-display/annual-report-account-mapping-german-display.component';
import { AccountMappingSwissDisplayComponent } from './microservices/ms-annual-reports/account-mapping/account-mapping-swiss-display/annual-report-account-mapping-swiss-display.component';
import { AccountProofGermanPrintPreviewPage } from './microservices/ms-annual-reports/account-proof-print-preview/account-proof-german-print-preview/account-proof-german-print-preview.page';
import { AccountProofSwissPrintPreviewPage } from './microservices/ms-annual-reports/account-proof-print-preview/account-proof-swiss-print-preview/account-proof-swiss-print-preview.page';
import { AdjustmentsAndReclassesPage } from './microservices/ms-annual-reports/adjustments-and-reclasses/adjustments-and-reclasses.page';
import { IncomeAccountSelectionPopupComponent } from './microservices/ms-annual-reports/adjustments-and-reclasses/income-account-selection-popup/income-account-selection-popup.component';
import { FinancialStatementImportModule } from './microservices/ms-annual-reports/annual-report-financial-statement-import/annualReportFinancialStatementImport.module';
import { AnnualReportFinancialStatementImportPage } from './microservices/ms-annual-reports/annual-report-financial-statement-import/annualReportFinancialStatementImport.page';
import { AnnualReportSubPage } from './microservices/ms-annual-reports/annual-report-sub-page';
import { AnnualReportModule } from './microservices/ms-annual-reports/annualreport.module';
import { AnnualReportPage } from './microservices/ms-annual-reports/annualreport.page';
import { AnnualReportCSFPage } from './microservices/ms-annual-reports/cash-flow-statement/cash-flow-statement.page';
import { ComplementaryReportsGermanPrintPreviewPage } from './microservices/ms-annual-reports/complementary-report-print-preview/complementary-report-german-print-preview/comp-report-german-print-preview.page';
import { ComplementaryReportsSwissPrintPreviewPage } from './microservices/ms-annual-reports/complementary-report-print-preview/complementary-report-swiss-print-preview/comp-report-swiss-print-preview.page';
import { ComplementaryReportsPage } from './microservices/ms-annual-reports/complementary-reports/complementary-reports.page';
import { AnnualReportCoverDisplayComponent } from './microservices/ms-annual-reports/cover-page/cover.display.component';
import { AnnualReportCoverPage } from './microservices/ms-annual-reports/cover-page/cover.page';
import { EquityMovementScheduleGermanPrintPreviewPage } from './microservices/ms-annual-reports/equity-movement-schedule-print-preview/equity-movement-schedule-german-print-preview/equity-german-print-preview.page';
import { EquityMovementScheduleSwissPrintPreviewPage } from './microservices/ms-annual-reports/equity-movement-schedule-print-preview/equity-movement-schedule-swiss-print-preview/equity-swiss-print-preview.page';
import { AnnualReportEquityMovementSchedulePage } from './microservices/ms-annual-reports/equity-movement-schedule/equity-movemenet-schedule.page';
import { FinancialClosingProcessModule } from './microservices/ms-annual-reports/financial-closing-process/financialClosingProcess.module';
import { FinancialStatementEditModule } from './microservices/ms-annual-reports/financial-statement-edit/financial-statement-edit.module';
import { FinancialStatementEditPage } from './microservices/ms-annual-reports/financial-statement-edit/financial-statement-edit.page';
import { AnnualReportFixedAssetsSchedulePage } from './microservices/ms-annual-reports/fixed-assets-schedule/fixed-assets-schedule.page';
import { AnnualReportGeneralInformationV2Page } from './microservices/ms-annual-reports/general-information-v2/annual-report-general-information-v2.page';
import { PrintPageHelper } from './microservices/ms-annual-reports/helpers/printPage.helper';
import { FinancialStatementAccountMappingFormComponent } from './microservices/ms-annual-reports/import-balance-sheet/financial-statement-account-mapping-form/financial-statement-account-mapping-form.component';
import { FinancialStatementImportBalanceSheetComponent } from './microservices/ms-annual-reports/import-balance-sheet/financial-statement-import/financial-statement-import-balanceSheet.component';
import { FxConversionTableComponent } from './microservices/ms-annual-reports/import-balance-sheet/fx-rates/fx-conversion-table/fx-conversion-table.component';
import { FxHistoricalModalComponent } from './microservices/ms-annual-reports/import-balance-sheet/fx-rates/fx-historical-modal/fx-historical-modal.component';
import { FxRateFileGuideComponent } from './microservices/ms-annual-reports/import-balance-sheet/fx-rates/fx-rate-file-guide/fx-rate-file-guide.component';
import { FxRatesComponent } from './microservices/ms-annual-reports/import-balance-sheet/fx-rates/fx-rates.component';
import { GroupingMaskingComponent } from './microservices/ms-annual-reports/import-balance-sheet/grouping-masking/grouping-masking.component';
import { NewAccountNestedModalComponent } from './microservices/ms-annual-reports/import-balance-sheet/grouping-masking/new-account-nested-modal/new-account-nested-modal.component';
import { NewGroupNestedModalComponent } from './microservices/ms-annual-reports/import-balance-sheet/grouping-masking/new-group-nested-modal/new-group-nested-modal.component';
import { ReclassNewTargetChoiceComponent } from './microservices/ms-annual-reports/import-balance-sheet/grouping-masking/reclass-new-target-choice/reclass-new-target-choice.component';
import { ReclassificationCellComponent } from './microservices/ms-annual-reports/import-balance-sheet/grouping-masking/reclassification-cell/reclassification-cell.component';
import { ReclassificationConflictsComponent } from './microservices/ms-annual-reports/import-balance-sheet/grouping-masking/reclassification-conflicts/reclassification-conflicts.component';
import { ReclassificationModalComponent } from './microservices/ms-annual-reports/import-balance-sheet/grouping-masking/reclassification-modal/reclassification-modal.component';
import { ReclassificationPreviewComponent } from './microservices/ms-annual-reports/import-balance-sheet/grouping-masking/reclassification-preview/reclassification-preview.component';
import { FixedAssetsScheduleGermanMapper } from './microservices/ms-annual-reports/mappers/fixed-assets-schedule-german-mapper';
import { FixedAssetsScheduleSwissMapper } from './microservices/ms-annual-reports/mappers/fixed-assets-schedule-swiss-mapper';
import { PdfTronGermanMapper } from './microservices/ms-annual-reports/mappers/pdf-tron-german-mapper';
import { PdfTronSwissMapper } from './microservices/ms-annual-reports/mappers/pdf-tron-swiss-mapper';
import { AccountProofMapper } from './microservices/ms-annual-reports/mappers/pdf.accountProof.mapper';
import { NotesPage } from './microservices/ms-annual-reports/notes/notes.page';
import { AnnualReportPrintPage } from './microservices/ms-annual-reports/print-page/annualReportPrint.page';
import { GermanPrintPreviewPage } from './microservices/ms-annual-reports/print-page/german-print-preview/german-print-preview.page';
import { LockReportComponent } from './microservices/ms-annual-reports/print-page/lock-report/lock-report.component';
import { SealAnnualReportComponent } from './microservices/ms-annual-reports/print-page/seal-annual-report/seal-annual-report.component';
import { SealStatusBannerComponent } from './microservices/ms-annual-reports/print-page/seal-status-banner/seal-status-banner.component';
import { SwissPrintPreviewPage } from './microservices/ms-annual-reports/print-page/swiss-print-preview/swiss-print-preview.page';
import { UnsealAnnualReportComponent } from './microservices/ms-annual-reports/print-page/unseal-annual-report/unseal-annual-report.component';
import { ProjectsOverviewPage } from './microservices/ms-annual-reports/projects-overview/projects-overview.page';
import { PersonProjectsOverviewPage } from './microservices/ms-annual-reports/person-projects-overview/person-projects-overview.page';
import { PersonMandateManagementPage } from './microservices/ms-annual-reports/person-mandate-management/person-mandate-management.page';
import { AnnualReportSignatureDisplayComponent } from './microservices/ms-annual-reports/signature/signature.display.component';
import { AnnualReportSignaturePage } from './microservices/ms-annual-reports/signature/signature.page';
import { AnnualReportTocDisplayComponent } from './microservices/ms-annual-reports/toc-page/toc.display.component';
import { AnnualReportTocPage } from './microservices/ms-annual-reports/toc-page/toc.page';
import { EnablingClosingProcessModule } from './microservices/ms-closing-process-enabling/enabling-closing-process.module';
import { EnablingClosingProcess } from './microservices/ms-closing-process-enabling/enabling-closing-process/enabling-closing-process.component';
import { AddOrEditCompanyPage } from './microservices/ms-companies/add-edit-company/add-edit-company.page';
import { AddOrEditCompanyPage2 } from './microservices/ms-companies/add-edit-company/add-edit-company2.page';
import { CompaniesListPage } from './microservices/ms-companies/companies-list/companies-list.page';
import { CompaniesModule } from './microservices/ms-companies/companies.module';
import { CompaniesPage } from './microservices/ms-companies/companies.page';
import { CompanyDetailsModule } from './microservices/ms-companies/company-details/company-details.module';
import { CompanyDetailsPage } from './microservices/ms-companies/company-details/company-details.page';
import { PersonsPage } from './microservices/ms-persons/persons.page';
import { PersonsListPage } from './microservices/ms-persons/persons-list/persons-list.page';
import { AddEditPersonPage } from './microservices/ms-persons/add-edit-person/add-edit-person.page';
import { PersonDetailsPage } from './microservices/ms-persons/person-details/person-details.page';
import { PersonProjectsPage } from './microservices/ms-persons/person-projects/person-projects.page';
import { CompanyIntegrationsPage } from './microservices/ms-companies/company-integrations/company-integrations.page';
import { CompanyProjectsOverviewPage } from './microservices/ms-companies/company-projects-overview/company-projects-overview.page';
import { CompanyValuationModule } from './microservices/ms-company-valuation/companyValuation.module';
import { CompanyValuationPage } from './microservices/ms-company-valuation/companyValuation.page';
import { ValuationReportPage } from './microservices/ms-company-valuation/valuation-report/valuation-report.page';
import { CustomersOverviewPage } from './microservices/ms-customers/customers-overview.page';
import { OrganizationProfilePage } from './microservices/ms-organization/organization-profile/organization-profile.page';
import { ClosingWorkflowConfiguratorPage } from './microservices/ms-organization/organization-settings/closing-workflow-configurator/closing-workflow-configurator.page';
import { ClosingWorkflowOverviewPage } from './microservices/ms-organization/organization-settings/closing-workflow-overview/closing-workflow-overview.page';
import { NoAccessPage } from './microservices/ms-organization/organization-settings/no-access.page';
import { OrganizationSettingsModule } from './microservices/ms-organization/organization-settings/organization-settings.module';
import { OrganizationSettingsPage } from './microservices/ms-organization/organization-settings/organization-settings.page';
import { TextSnippetsPageComponent } from './microservices/ms-organization/organization-settings/text-snippets/text-snippets.page';
import { OrganizationUsersPage } from './microservices/ms-organization/organization-users/organization-users.page';
import { OrganizationModule } from './microservices/ms-organization/organization.module';
import { OrganizationPage } from './microservices/ms-organization/organization.page';
import { AnnotationsPage } from './microservices/ms-tasks/annotations/annotations.page';
import { TasksOverviewPage } from './microservices/ms-tasks/tasks-overview/tasks-overview.page';
import { TasksModule } from './microservices/ms-tasks/tasks.module';
import { TasksPage } from './microservices/ms-tasks/tasks.page';
import { UserAccountModule } from './microservices/ms-users/user-account.module';
import { UserAccountPage } from './microservices/ms-users/user-account.page';
import { AuthModulePage } from './modules/auth/auth-module.page';
import { AuthModule } from './modules/auth/auth.module';
import { HomePage } from './modules/home/home-module.page';
import { HomeModule } from './modules/home/home.module';
import { ANNUALREPORT_ORCHESTRATOR_TOKEN, AnnualReportOrchestrator } from './orchestrator/annualreport.orchestrator';
import { AcceptInvitationPage } from './pages/accept-invitation/accept-invitation.page';
import { BasePageComponent } from './pages/base.page';
import { BexioErrorPage } from './pages/bexio/bexio-error/bexio-error.page';
import { EditUserGeneralInformationPage } from './pages/edit-user/general-information/edit-user-general-information.page';
import { EditUserSecurityPage } from './pages/edit-user/security/edit-user-security.page';
import { LoginPage } from './pages/login/login.page';
import { LogoutPage } from './pages/login/logout.page';
import { NotFoundPage } from './pages/not-found/not-found.page';
import { OAuthAuthorizePage } from './pages/oauth-authorize/oauth-authorize.page';
import { PasswordsBasePage } from './pages/passwords/passwords-base.page';
import { ResetPasswordPage } from './pages/passwords/reset-password/reset-password.page';
import { SetPasswordPage } from './pages/passwords/set-password/set-password.page';
import { PlaceholderSubnavPage } from './pages/placeholder/placeholder-subnav.page';
import { PlaceholderPage } from './pages/placeholder/placeholder.page';
import { PopUpComponent } from './pages/popUp/popUp';
import { RedirectPage } from './pages/redirect/redirect.page';
import { ResetAccountPage } from './pages/reset/reset.page';
import { SignupPage } from './pages/signup-process';
import { ConfirmAccountPage } from './pages/signup-process/confirm-account/confirm-account.page';
import { SurveyPopupComponent } from './pages/survey-popup/survey-popup.component';
import { CanDeactivateGuard } from './shared/can-deactivate.guard';
import { ReleaseHighlightsModalComponent } from './shared/components/release-highlights-modal/release-highlights-modal.component';
import { HttpErrorInterceptor } from './shared/interceptors/error.interceptor';
import { ThrottleRequestsInterceptor } from './shared/interceptors/throttle-requests.interceptor';
import { NgrxLocalStorageService } from './shared/services/ngrxlocalstorage.store.service';
import { StoreService } from './shared/services/store.service';
import { USER_INTERCEPTOR } from './shared/services/userService.interface';
import { ContentAreaComponent } from './skeleton/content-area/content-area.component';
import * as fromAppState from './state/app.state.reducer';
import { TEST_CONFIG_TOKEN, TEST_LOCAL_STORAGE_KEY, TEST_STORAGE_KEYS } from './state/app.state.tokens';
import { storageMetaReducer } from './state/storage.metareducer';

const usersConfig: UsersConfig = {
    apiGateway: environment.modeso.apiGateway,
    serviceId: 'users',
};

const annualReportConfig: AumicoAnnualReportConfig = {
    apiGateway: environment.modeso.apiGateway,
    serviceId: 'annualreport',
};

const cpmmConfig: CpmmConfig = {
    apiGateway: environment.modeso.apiGateway,
    serviceId: 'cpmm',
};

const projectsConfig: ProjectsConfig = {
    apiGateway: environment.modeso.apiGateway,
    serviceId: 'projects',
};

const companiesConfig: AumicoCompaniesConfig = {
    apiGateway: environment.modeso.apiGateway,
    serviceId: 'companies',
};

const notesConfig: AumicoNotesConfig = {
    apiGateway: environment.modeso.apiGateway,
    serviceId: 'notes service',
};

const settingsConfig: AumicoSettingsConfig = {
    apiGateway: environment.modeso.apiGateway,
    serviceId: 'settings',
    httpOptions: {
        headers: {},
    },
};

const templatesConfig: AumicoTemplatesConfig = {
    apiGateway: environment.modeso.apiGateway,
    serviceId: 'templates',
};

const aiAgentsConfig: AumicoAiAgentsConfig = {
    apiGateway: environment.modeso.apiGateway,
    wsGateway: environment.modeso.wsGateway,
};

const coreConfig: AumicoAnnualReportConfig = {
    apiGateway: environment.modeso.apiGateway,
    serviceId: 'core',
};

const integrationsConfig: AumicoIntegrationsConfig = {
    apiGateway: environment.modeso.apiGateway,
};

// REV-21376 — the ABCP data layer (first real ABCP NgRx slice). Separate from AbcpUiModule, which
// holds the PURE views: those inject no Store and do no HTTP, so the slice must not ride along with
// them.
const abcpConfig: AumicoAbcpConfig = {
    apiGateway: environment.modeso.apiGateway,
};

export function getMetaReducers(
    saveKeys: string[],
    localStorageKey: string,
    storageService: StoreService,
): MetaReducer<any>[] {
    return [storageMetaReducer(saveKeys, localStorageKey, storageService)];
}

export function getCoursesConfig(saveKeys: string[], localStorageKey: string, storageService: NgrxLocalStorageService) {
    return { metaReducers: [storageMetaReducer(saveKeys, localStorageKey, storageService)] };
}

const ANNUAL_REPORT_PAGES = [
    ProjectsOverviewPage,
    PersonProjectsOverviewPage,
    PersonMandateManagementPage,
    FinancialStatementEditPage,
    AnnualReportGeneralInformationV2Page,
    AnnualReportFinancialStatementImportPage,
    AnnualReportCSFPage,
    AnnualReportAOPPage,
    AnnualReportPrintPage,
    SwissPrintPreviewPage,
    GermanPrintPreviewPage,
    AnnualReportSubPage,
    AdjustmentsAndReclassesPage,
    AnnualReportEquityMovementSchedulePage,
    EquityMovementScheduleGermanPrintPreviewPage,
    EquityMovementScheduleSwissPrintPreviewPage,
    AnnualReportFixedAssetsSchedulePage,
    ComplementaryReportsGermanPrintPreviewPage,
    ComplementaryReportsSwissPrintPreviewPage,
    LockReportComponent,
    UnsealAnnualReportComponent,
    SealStatusBannerComponent,
];

@NgModule({
    declarations: [
        AppComponent,
        AppIntroComponent,
        AuthModulePage,
        HomePage,
        PageNotFoundComponent,
        NotFoundPage,
        OAuthAuthorizePage,
        BasePageComponent,
        ContentAreaComponent,
        RedirectPage,
        LoginPage,
        LogoutPage,
        PopUpComponent,
        SurveyPopupComponent,
        PlaceholderPage,
        PlaceholderSubnavPage,
        CustomersOverviewPage,
        SignupPage,
        RandomQuestionWithActionButtonComponent,
        WelcomePageFooterComponent,
        FinancialStatementAccountMappingFormComponent,
        FinancialStatementImportBalanceSheetComponent,
        FxConversionTableComponent,
        FxHistoricalModalComponent,
        FxRateFileGuideComponent,
        FxRatesComponent,
        GroupingMaskingComponent,
        ReclassificationCellComponent,
        ReclassificationModalComponent,
        NewAccountNestedModalComponent,
        NewGroupNestedModalComponent,
        ReclassNewTargetChoiceComponent,
        ReclassificationConflictsComponent,
        ReclassificationPreviewComponent,
        SideHeadingComponent,
        SideParagraphComponent,
        SideListItemComponent,
        SideListComponent,
        LoadingSpinnerComponent,
        ConfirmAccountPage,
        ResetAccountPage,
        AppButtonComponent,
        AnnualReportPage,
        ANNUAL_REPORT_PAGES,
        CompanyProjectsOverviewPage,
        CompaniesPage,
        CompanyValuationPage,
        EnablingClosingProcess,
        ValuationReportPage,
        UserAccountPage,
        NotesPage,
        EditUserGeneralInformationPage,
        EditUserSecurityPage,
        OrganizationPage,
        OrganizationProfilePage,
        OrganizationSettingsPage,
        OrganizationUsersPage,
        TextSnippetsPageComponent,
        AnnualReportCoverDisplayComponent,
        AnnualReportCoverPage,
        AnnualReportTocDisplayComponent,
        AcceptPolicyDialogComponent,
        ValuationReportDialogComponent,
        AnnualReportTocPage,
        AnnualReportSignaturePage,
        AnnualReportSignatureDisplayComponent,
        AccountMappingSwissDisplayComponent,
        AccountMappingGermanDisplayComponent,
        AccountProofGermanPrintPreviewPage,
        AccountProofSwissPrintPreviewPage,
        BexioErrorPage,
        CompaniesListPage,
        AddOrEditCompanyPage,
        AddOrEditCompanyPage2,
        IncomeAccountSelectionPopupComponent,
        ReorderReportSectionsDialogComponent,
        InviteDialogComponent,
        ArTemplatesListDialogComponent,
        ComplementaryReportsPage,
        ResetPasswordPage,
        SetPasswordPage,
        AcceptInvitationPage,
        PasswordsBasePage,
        InvitationExpiredPage,
        ConfirmShareReportDialogComponent,
        ReferralTeaserComponent,
        AcceptPolicyToContinueUsingAumicoDialogComponent,
        ValuationOrderConfirmationDialogComponent,
        ValuationReportAnnouncementDialogComponent,
        CompanyDetailsPage,
        // REV-22155: persons
        PersonsPage,
        PersonsListPage,
        AddEditPersonPage,
        PersonDetailsPage,
        PersonProjectsPage,
        CompanyIntegrationsPage,
        SealAnnualReportComponent,
        AnnotationsPage,
        TasksPage,
        TasksOverviewPage,
        ClosingWorkflowOverviewPage,
        ClosingWorkflowConfiguratorPage,
        ClosingWorkflowPage,
        MandateManagementPage,
        CpmmPage,
        AbcpPage,
        TasksAndClosingAdvertisingPage,
        NoAccessPage,
        SwitchToAutomaticTransferOfAnnualResultComponent,
        ReleaseHighlightsModalComponent
    ],
    imports: [
        // PlatformModule,
        // FlexLayoutModule,
        AumicoComponentsModule,
        AumicoCpmmModule,
        AumicoProjectsModule,
        I18NextModule.forRoot(),
        BrowserModule.withServerTransition({ appId: 'serverApp' }),
        StoreModule.forRoot(fromAppState.reducer, {
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
            },
        }),
        StoreModule.forFeature(fromAppState.appStateFeatureKey, fromAppState.reducer, TEST_CONFIG_TOKEN),
        StoreDevtoolsModule.instrument({
            maxAge: 25, // Retains last 25 states
            logOnly: environment.production, // Restrict extension to log-only mode
            // Also active in production: keep vendor connect secrets out of the action history (REV-22536, REV-21119).
            actionSanitizer: redactConnectSecretsForDevtools,
        }),
        EffectsModule.forRoot([]),
        QuillModule.forRoot({}),
        HttpClientModule,
        AppRoutingModule,
        CommonModule,
        // SharedModule,
        FormsModule,
        ReactiveFormsModule,
        AumicoCoreModule.forRoot(coreConfig),
        AumicoLayoutModule2,
        UsersModule.forRoot(usersConfig),
        AumicoAnnualReportModule.forRoot(annualReportConfig),
        AumicoCpmmModule.forRoot(cpmmConfig),
        AumicoProjectsModule.forRoot(projectsConfig),
        AumicoNotesModule.forRoot(notesConfig),
        AumicoLibSettingsFeModule.forRoot(settingsConfig),
        AumicoCompaniesModule.forRoot(companiesConfig),
        AumicoTemplatesModule.forRoot(templatesConfig),
        AumicoAiAgentsModule.forRoot(aiAgentsConfig),
        AumicoLibIntegrationsFeModule.forRoot(integrationsConfig),
        AbcpDataModule.forRoot(abcpConfig),
        FinancialClosingProcessModule,
        BrowserAnimationsModule,
        MatDialogModule,
        MatSelectModule,
        MatNativeDateModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatTableModule,
        MatPaginatorModule,
        MatListModule,
        MatSnackBarModule,
        MatCheckboxModule,
        MatTabsModule,
        MatProgressSpinnerModule,
        MatButtonModule,
        MatChipsModule,
        MatExpansionModule,
        MatStepperModule,
        MatIconModule,
        MatButtonToggleModule,
        DragDropModule,
        MatAutocompleteModule,
        AuthModule,
        HomeModule,
        AnnualReportModule,
        FinancialStatementEditModule,
        CpmmModule,
        AbcpModule,
        FinancialStatementImportModule,
        OrganizationModule,
        OrganizationSettingsModule,
        CompaniesModule,
        CompanyValuationModule,
        EnablingClosingProcessModule,
        UserAccountModule,
        CompanyDetailsModule,
        FinancialClosingProcessModule,
        TasksModule,
        MatPasswordStrengthModule.forRoot(),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => {
            const auth = getAuth();
            if (
                environment.useFirebaseAuthenticationEmulator != null &&
                environment.useFirebaseAuthenticationEmulator &&
                environment.firebaseAuthenticationEmulatorUrl != null &&
                environment.firebaseAuthenticationEmulatorUrl.length !== 0
            ) {
                connectAuthEmulator(auth, environment.firebaseAuthenticationEmulatorUrl, { disableWarnings: true });
            }
            return auth;
        }),
        GoogleTagManagerModule.forRoot({
            id: environment.googleTagManagerId,
        }),
    ],
    providers: [
        I18N_PROVIDERS,
        // FX (REV-21512): the notes display components inject this to derive the super-table
        // print FX bundle themselves (reclass pattern) — the engine stays app-side.
        { provide: FX_NOTES_PRINT_ENGINE, useClass: FxNotesPrintEngineService },
        { provide: HTTP_INTERCEPTORS, useClass: ThrottleRequestsInterceptor, multi: true },
        // REV-21869 (#162, spec:GH-162 S3) — the reconciliation-poll cadence override. The LIBRARY's
        // own token default is the production cadence (`AumicoAbcpPollIntervalsToken` cannot see
        // `environment`, which exists only here); this is the ONE place the app may shorten it, and
        // only outside a production build — the same gate REV-21960/REV-21057 use for their own
        // window-read demo seams, so a production bundle never contains this factory either.
        // `window.__aumicoAbcpPollIntervals` is the exact key+shape `shortenPollIntervals` in
        // `e2e/fixtures/abcp-reconciliation.ts` writes via `addInitScript`, before any app script runs.
        // ROUND 4 (SRR F3) — the value is no longer taken verbatim (`??` only ever caught
        // `null`/`undefined`): `resolveAumicoAbcpPollIntervals` refuses anything below the floor
        // (`AUMICO_ABCP_POLL_INTERVAL_FLOOR_MS`) and falls back to the production cadence instead.
        // REV-22118 — plus the ONE production build that opts in (`abcpPollIntervalOverride`, true
        // only in `environment.testing.ts`): the abcp suite runs against that build, and without
        // the override its poll cases wait on the 10 s / 30 s production cadence and time out. Every
        // other production build — prod, staging, patch — keeps the gate exactly as it was.
        ...(environment.production && !environment.abcpPollIntervalOverride
            ? []
            : [
                  {
                      provide: AumicoAbcpPollIntervalsToken,
                      useFactory: () =>
                          resolveAumicoAbcpPollIntervals(
                              (window as unknown as Record<string, unknown>)['__aumicoAbcpPollIntervals'],
                          ),
                  },
              ]),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        Sentry.TraceService,
        {
            provide: APP_INITIALIZER,
            useFactory: (traceService: Sentry.TraceService) => () => { },
            deps: [Sentry.TraceService],
            multi: true,
        },
        PrintPageHelper,
        PdfTronSwissMapper,
        PdfTronGermanMapper,
        AccountProofMapper,
        FixedAssetsScheduleSwissMapper,
        FixedAssetsScheduleGermanMapper,
        { provide: HTTP_INTERCEPTORS, useClass: AuthHandler, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
        { provide: USER_INTERCEPTOR, useClass: LocalStorageService },
        {
            provide: ROOT_STORAGE_KEYS,
            useValue: [
                'aumicoUsersMicroservice',
                'aumicoAnnualReportMicroservice',
                'aumicoCompaniesMicroservice',
                'aumicoProjectsOverviewFeature',
                CPMM_UI_FEATURE, // <-- Add the UI state key for local storage
            ],
        },
        { provide: ROOT_LOCAL_STORAGE_KEY, useValue: '__app_storage__' },
        {
            provide: USER_PROVIDED_META_REDUCERS,
            deps: [ROOT_STORAGE_KEYS, ROOT_LOCAL_STORAGE_KEY, NgrxLocalStorageService],
            useFactory: getMetaReducers,
        },
        { provide: TEST_LOCAL_STORAGE_KEY, useValue: '__test_storage__' },
        { provide: TEST_STORAGE_KEYS, useValue: ['user', 'test'] },
        {
            provide: TEST_CONFIG_TOKEN,
            deps: [TEST_STORAGE_KEYS, TEST_LOCAL_STORAGE_KEY, NgrxLocalStorageService],
            useFactory: getCoursesConfig,
        },
        {
            provide: MatDialogRef,
            useValue: {},
        },
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: { displayDefaultIndicatorType: false },
        },
        {
            provide: ANNUALREPORT_ORCHESTRATOR_TOKEN,
            useClass: AnnualReportOrchestrator,
        },
        {
            provide: LANGUAGE_PROVIDER,
            useExisting: LocalStorageService,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LanguageInterceptor,
            multi: true,
        },
        CanDeactivateGuard,
        AutomaticTransferOfAnnualResultService
    ],
    bootstrap: [AppComponent],
})
export class AppModule { }
